import { Link } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import {
  BookOpen, Layers, Palette, Layout, FolderTree, Rocket,
  Zap, CheckCircle, ArrowRight
} from 'lucide-react';
import { Header } from '../../../shared/components';
import { tutorialLevels, getTotalTutorialCount } from '../../../shared/config/tutorials';

const HomePage = () => {
  const highlights = [
    { icon: Layers, label: '37+ Components', desc: 'Production-ready UI' },
    { icon: Layout, label: '6 Layouts', desc: 'Admin, Page, Auth...' },
    { icon: Palette, label: '5 Themes', desc: 'Light & dark modes' },
    { icon: Zap, label: 'CLI Tools', desc: 'Scaffold & generate' },
  ];

  const levelIcons = {
    foundation: BookOpen,
    theming: Palette,
    layouts: Layout,
    architecture: FolderTree,
    production: Rocket,
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
            <Zap className="w-4 h-4" />
            v1.4.0 - Now with prerender & optimize CLI
          </div>

          <h1 className="text-5xl md:text-6xl font-bold">
            Learn <span className="text-primary">UIKit</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Master the React component library with {getTotalTutorialCount()} hands-on tutorials.
            From basics to production deployment.
          </p>

          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <Link to="/tutorials">
              <Button size="lg" className="px-8">
                <BookOpen className="w-4 h-4 mr-2" />
                Start Learning
              </Button>
            </Link>
            <Link to="/sandbox">
              <Button size="lg" variant="outline" className="px-8">
                <Layers className="w-4 h-4 mr-2" />
                Try Components
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {highlights.map((item) => (
              <div key={item.label} className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="font-semibold">{item.label}</div>
                <div className="text-sm text-muted-foreground">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tutorial Levels */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Learning Path</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Progress through 5 levels - from installation to production deployment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tutorialLevels.map((level, index) => {
              const Icon = levelIcons[level.id as keyof typeof levelIcons] || BookOpen;
              return (
                <Card key={level.id} className="relative overflow-hidden hover:border-primary/50 transition-colors">
                  <div className="absolute top-4 right-4 w-8 h-8 bg-muted rounded-full flex items-center justify-center text-sm font-bold text-muted-foreground">
                    {index + 1}
                  </div>
                  <CardHeader>
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{level.title.replace(`Level ${index + 1}: `, '')}</CardTitle>
                    <CardDescription>{level.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {level.pages.slice(0, 3).map((page) => (
                        <li key={page.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-3 h-3 text-primary" />
                          {page.title}
                        </li>
                      ))}
                      {level.pages.length > 3 && (
                        <li className="text-sm text-muted-foreground">
                          +{level.pages.length - 3} more...
                        </li>
                      )}
                    </ul>
                    <Link to={level.pages[0].path}>
                      <Button variant="outline" size="sm" className="w-full">
                        Start Level {index + 1}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}

            {/* Sandbox Card */}
            <Card className="border-dashed hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mb-2">
                  <Layers className="w-5 h-5 text-accent" />
                </div>
                <CardTitle className="text-lg">Component Sandbox</CardTitle>
                <CardDescription>Interactive playground for all 37 components</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-3 h-3 text-accent" />
                    Live preview
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-3 h-3 text-accent" />
                    Copy-paste code
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-3 h-3 text-accent" />
                    Prop controls
                  </li>
                </ul>
                <Link to="/sandbox">
                  <Button variant="outline" size="sm" className="w-full">
                    Open Sandbox
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why UIKit */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why UIKit?</h2>
            <p className="text-muted-foreground">What makes UIKit different from other React UI libraries</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Speed
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>30-second setup</strong> with CLI scaffolding</li>
                <li>• <strong>4 project templates</strong> - single to enterprise</li>
                <li>• <strong>Code generation</strong> for pages, components, features</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Layout className="w-5 h-5 text-primary" />
                Layouts
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>6 production layouts</strong> included</li>
                <li>• AdminLayout, PageLayout, AuthLayout</li>
                <li>• MobileLayout, PopupLayout, BlankLayout</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary" />
                Theming
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>5 professional themes</strong> out of the box</li>
                <li>• <strong>OKLCH color science</strong> for perfect contrast</li>
                <li>• <strong>Dark mode</strong> with one prop</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Rocket className="w-5 h-5 text-primary" />
                Production
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>SEO pre-rendering</strong> for SPAs</li>
                <li>• <strong>Image optimization</strong> (WebP conversion)</li>
                <li>• <strong>One-command deploy</strong> to GitHub Pages</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Start?</h2>
          <p className="text-muted-foreground">
            Begin your UIKit journey with our step-by-step tutorials
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/tutorials/foundation/what-is-uikit">
              <Button size="lg" className="px-8">
                Start Tutorial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <p className="text-sm text-muted-foreground pt-4">
            Or install directly: <code className="bg-muted px-2 py-1 rounded">npm install @voilajsx/uikit</code>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>UIKit by <strong>VoilaJSX</strong> - MIT License</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
